// Rollout flag for the physical (WebGPU) paint engine in the Canvas Paint Tool.
// On: ?physicalPaint=1 once on a Canvas URL, or localStorage 'vx.physicalPaint' = '1'.
// Off: ?physicalPaint=0 (remembered as '0'). The Voxemera desktop app (Tauri) has
// its own WebView storage, so there the engine is on by default unless turned off.
// Without the flag, or without WebGPU, the Paint Tool keeps the original oil paint backend.

import { isWebGPUAvailable, requestPaintDevice } from '../gpu/WebGPUPaintEngine.js';

const FLAG_KEY = 'vx.physicalPaint';
let devicePromise = null;
let readyDevice = null;
const deviceListeners = new Set();
const notifyDevice = () => deviceListeners.forEach((listener) => { try { listener(readyDevice); } catch (error) { /* listener errors stay local */ } });

function isDesktopApp() {
  return Boolean(window.__TAURI__ || window.__TAURI_INTERNALS__);
}

export function isPhysicalPaintEnabled() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('physicalPaint') === '1') window.localStorage.setItem(FLAG_KEY, '1');
    if (params.get('physicalPaint') === '0') window.localStorage.setItem(FLAG_KEY, '0');
    const stored = window.localStorage.getItem(FLAG_KEY);
    if (stored === '1') return true;
    if (stored === '0') return false;
    return isDesktopApp();
  } catch (error) {
    return false;
  }
}

// Starts acquiring the GPU device early (the Canvas core is created synchronously).
export function preparePhysicalPaintDevice() {
  if (devicePromise) return devicePromise;
  if (typeof window === 'undefined' || !isPhysicalPaintEnabled() || !isWebGPUAvailable()) return null;
  devicePromise = requestPaintDevice()
    .then((gpu) => {
      readyDevice = gpu;
      if (gpu) gpu.device.lost.then(() => { readyDevice = null; devicePromise = null; notifyDevice(); });
      notifyDevice();
      return gpu;
    })
    .catch(() => null);
  return devicePromise;
}

export function getReadyPaintDevice() {
  return readyDevice;
}

// Calls listener(device | null) whenever the device becomes ready or is lost.
// Returns the unsubscribe function.
export function subscribePaintDevice(listener) {
  deviceListeners.add(listener);
  return () => deviceListeners.delete(listener);
}
